/**
 * Locale for Intl / toLocale*() formatting that follows the app's UI language
 * (kept in sync by LanguageProvider), instead of the browser's language.
 */

import { AppLanguages } from './AppLanguages.jsx';

// Arabic & Farsi keep Latin digits so numbers match inputs, prices and IDs.
const INTL_LOCALES = {
    ar: 'ar-u-nu-latn',
    fa: 'fa-u-nu-latn',
};

const SUPPORTED = new Set(AppLanguages.map((lang) => lang.code));

/** Read `?lang=` from the URL (public-site → dashboard handoff). */
export function readLanguageFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        const code = String(params.get('lang') || '')
            .trim()
            .toLowerCase();
        return SUPPORTED.has(code) ? code : null;
    } catch {
        return null;
    }
}

/** Persist language and drop `lang` from the query string (keeps other params). */
export function consumeLanguageFromUrl(code) {
    if (!code || !SUPPORTED.has(code)) return;
    try {
        localStorage.setItem('appLanguage', code);
        const url = new URL(window.location.href);
        if (!url.searchParams.has('lang')) return;
        url.searchParams.delete('lang');
        const next = `${url.pathname}${url.search}${url.hash}`;
        window.history.replaceState(window.history.state, '', next);
    } catch {
        // ignore
    }
}

// Start from URL handoff, then the saved choice (LanguageProvider's `appLanguage` key)
// so anything rendered before the provider (e.g. root ErrorBoundary) already uses
// the right language.
function readSavedLanguage() {
    try {
        const fromUrl = readLanguageFromUrl();
        if (fromUrl) {
            localStorage.setItem('appLanguage', fromUrl);
            return fromUrl;
        }
        const saved = localStorage.getItem('appLanguage');
        return SUPPORTED.has(saved) ? saved : 'en';
    } catch {
        return 'en';
    }
}

let uiLanguage = readSavedLanguage();

/** Called by LanguageProvider on every render so formatting is correct on the same render. */
export function setUiLanguage(code) {
    if (code) uiLanguage = code;
}

/** UI language code, e.g. "en", "tr", "ar", "fa", "ru". */
export function getUiLanguage() {
    return uiLanguage;
}

/** BCP 47 locale to pass to Intl.* and toLocale*String(). */
export function getUiLocale() {
    return INTL_LOCALES[uiLanguage] || uiLanguage;
}
